import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ApBaseComponent, NavigationScrollingService } from 'webcommon/core-ui';
import { FindControlIdService } from 'webcommon/legacy-common';
import { FindControlBehaviorSecurityDto, SecurityUserGroupDto } from 'webcommon/shared';
// eslint-disable-next-line no-restricted-imports
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, NgForm } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'ap-findcontrol-modify-security-tab-component',
  templateUrl: 'ap-findcontrol-modify-security-tab.component.html',
  styleUrls: ['ap-findcontrol-modify-security-tab.component.less'],
})
export class ApFindControlModifySecurityTabComponent extends ApBaseComponent implements OnInit {
  constructor(
    public findControlIdService: FindControlIdService,
    private navigationScrollingService: NavigationScrollingService,
  ) {
    super();
  }

  @Input() baseId: string;
  @Input() securityGroupUid: string;
  @Input() findControlBehaviorSecurity: FindControlBehaviorSecurityDto;
  @Input() securityTabFormGroup: UntypedFormGroup;
  @Input() form: NgForm;
  @Output() errorCountChange: EventEmitter<number> = new EventEmitter<number>();

  Id = '';
  securityUserGroupsFormArrayName = 'securityUserGroupsFormArray';
  userOrUserGroupIds: string[] = [];

  isLoading = false;
  loadingError = false;

  ngOnInit() {
    this.isLoading = true;

    this.setId();
    this.setupFormGroup();

    this.isLoading = false;
  }

  get securityUserGroupsFormArray(): UntypedFormArray {
    return this.securityTabFormGroup.controls[this.securityUserGroupsFormArrayName] as UntypedFormArray;
  }

  public invalidSecurityUserGroup(index: number): boolean {
    return this.securityUserGroupsFormArray.controls[index].hasError('invalidSecurityUserGroup');
  }

  private setupFormGroup(): void {
    this.securityTabFormGroup.addControl('securityUserGroupsFormArray', new UntypedFormArray([]));
    this.securityUserGroupsToFormGroups();
  }

  private securityUserGroupsToFormGroups(): void {
    if (this.findControlBehaviorSecurity.SecurityUserGroups) {
      this.findControlBehaviorSecurity.SecurityUserGroups.forEach(item => {
        this.userOrUserGroupIds.push(item.UserOrUserGroupId);

        const securityUserGroupGroupForm = this.addSecurityUserGroupFormGroup(item);
        this.securityUserGroupsFormArray.push(securityUserGroupGroupForm);
      });
    }
  }

  private addSecurityUserGroupFormGroup(item: SecurityUserGroupDto): UntypedFormGroup {
    const newFormGroup = new UntypedFormGroup({
      securityUserGroupIdFormControl: new UntypedFormControl(item.SecurityUserGroupId),
      isReadOnlySelectedFormControl: new UntypedFormControl(item.IsReadOnlySelected),
      isNewSelectedFormControl: new UntypedFormControl(item.IsNewSelected),
      isModifySelectedFormControl: new UntypedFormControl(item.IsModifySelected),
      isDeleteSelectedFormControl: new UntypedFormControl(item.IsDeleteSelected),
      isAllowSelectedFormControl: new UntypedFormControl(item.IsAllowSelected),
      isCompleteSelectedFormControl: new UntypedFormControl(item.IsCompleteSelected),
      isOverrideSelectedFormControl: new UntypedFormControl(item.IsOverrideSelected),
      isEveryoneFiltersSelectedFormControl: new UntypedFormControl(item.IsEveryoneFiltersSelected),
      isSelectSelectedFormControl: new UntypedFormControl(item.IsSelectSelected),
      isWarnSelectedFormControl: new UntypedFormControl(item.IsWarnSelected),
    });

    // statusChanges emits an event when the validation status is recalculated.
    newFormGroup.statusChanges.pipe(
      takeUntil(this.onDestroy$),
    ).subscribe(() => {
      // emit validation error count when validation status is recalcaluated.
      this.emitValidationErrorCount();
    });

    return newFormGroup;
  }

  public securityCheckboxChanged(fg: UntypedFormGroup, formControlName: string): void {
    if (!formControlName || formControlName.length <= 0) {
      return;
    }
    if (!fg) {
      return;
    }

    if (this.findControlBehaviorSecurity.SecurityPermissions?.IsReadOnlyAllowed) {
      if (fg.get(formControlName)?.value as boolean) {
        if (formControlName === 'isReadOnlySelectedFormControl') {
          this.uncheckNonReadOnlyCheckboxes(fg);
        } else {
          this.uncheckReadOnlyCheckbox(fg);
        }
      }
    }
  }
  private uncheckReadOnlyCheckbox(fg: UntypedFormGroup): void {
    fg.get('isReadOnlySelectedFormControl')?.setValue(false);
  }
  private uncheckNonReadOnlyCheckboxes(fg: UntypedFormGroup): void {
    fg.get('isNewSelectedFormControl')?.setValue(false);
    fg.get('isModifySelectedFormControl')?.setValue(false);
    fg.get('isDeleteSelectedFormControl')?.setValue(false);
    fg.get('isAllowSelectedFormControl')?.setValue(false);
    fg.get('isCompleteSelectedFormControl')?.setValue(false);
    fg.get('isOverrideSelectedFormControl')?.setValue(false);
    fg.get('isEveryoneFiltersSelectedFormControl')?.setValue(false);
    fg.get('isSelectSelectedFormControl')?.setValue(false);
    fg.get('isWarnSelectedFormControl')?.setValue(false);
  }

  public addRow() {
    this.userOrUserGroupIds.push('');

    this.securityUserGroupsFormArray.push(
      this.addSecurityUserGroupFormGroup(
        {
          SecurityUserGroupId: this.securityGroupUid,
          UserOrUserGroupId: '',
          IsReadOnlySelected: false,
          IsNewSelected: false,
          IsModifySelected: false,
          IsDeleteSelected: false,
          IsAllowSelected: false,
          IsCompleteSelected: false,
          IsOverrideSelected: false,
          IsEveryoneFiltersSelected: false,
          IsSelectSelected: false,
          IsWarnSelected: false,
        }
      )
    );

    this.securityTabFormGroup.markAsDirty();
  }

  public deleteRow(index: number) {
    let updateValidationErrorCount = false;
    if (this.securityUserGroupsFormArray.controls[index].invalid) {
      updateValidationErrorCount = true;
    }

    this.securityUserGroupsFormArray.removeAt(index);
    this.userOrUserGroupIds.splice(index, 1);
    this.securityTabFormGroup.markAsDirty();

    if (updateValidationErrorCount) {
      this.emitValidationErrorCount();
    }
  }

  public scrollToValidationError(): void {
    let invalidId: string | null = null;
    for (let index = 0; index < this.securityUserGroupsFormArray.controls.length; index++) {
      if (this.securityUserGroupsFormArray.controls[index].invalid) {
        invalidId = this.Id + '-user-usergroup-' + index;
        break;
      }
    }

    if (typeof invalidId === 'string' && invalidId.length > 0) {
      const options: ScrollIntoViewOptions = { behavior: 'smooth', block: 'center' };
      this.navigationScrollingService.scrollToId(invalidId, options);
    }
  }

  public emitValidationErrorCount(): void {
    let errorCount = 0;
    this.securityUserGroupsFormArray.controls.forEach(formGroup => {
      if (formGroup.invalid) {
        errorCount++;
      }
    });
    this.errorCountChange.emit(errorCount);
  }

  public save(): SecurityUserGroupDto[] {
    const dto: SecurityUserGroupDto[] = [];

    let index = 0;
    this.securityUserGroupsFormArray.controls.forEach(fg => {
      const securityUserGroup = new SecurityUserGroupDto();

      securityUserGroup.SecurityUserGroupId =
        fg.get('securityUserGroupId')?.value as string | null;

      securityUserGroup.UserOrUserGroupId = this.userOrUserGroupIds[index];

      securityUserGroup.IsReadOnlySelected =
        fg.get('isReadOnlySelectedFormControl')?.value as boolean;
      securityUserGroup.IsNewSelected =
        fg.get('isNewSelectedFormControl')?.value as boolean;
      securityUserGroup.IsModifySelected =
        fg.get('isModifySelectedFormControl')?.value as boolean;
      securityUserGroup.IsDeleteSelected =
        fg.get('isDeleteSelectedFormControl')?.value as boolean;
      securityUserGroup.IsAllowSelected =
        fg.get('isAllowSelectedFormControl')?.value as boolean;
      securityUserGroup.IsCompleteSelected =
        fg.get('isCompleteSelectedFormControl')?.value as boolean;
      securityUserGroup.IsOverrideSelected =
        fg.get('isOverrideSelectedFormControl')?.value as boolean;
      securityUserGroup.IsEveryoneFiltersSelected =
        fg.get('isEveryoneFiltersSelectedFormControl')?.value as boolean;
      securityUserGroup.IsSelectSelected =
        fg.get('isSelectSelectedFormControl')?.value as boolean;
      securityUserGroup.IsWarnSelected =
        fg.get('isWarnSelectedFormControl')?.value as boolean;

      dto.push(securityUserGroup);
      index++;
    });

    return dto;
  }

  private setId() {
    this.Id = `${this.baseId}-security-tab`;
  }
}
